import React, { useState, useEffect } from 'react';
import { format, parse, isBefore, startOfToday } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { fetchBookedTimeSlots } from '../../services/api';

interface TimeSlotPickerProps {
  onTimeSelect: (time: string) => void;
  selectedDate: Date;
  settings?: {
    business_hours_start?: string;
    business_hours_end?: string;
    time_slot_duration?: number;
  };
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({ 
  onTimeSelect, 
  selectedDate,
  settings 
}) => {
  const [loading, setLoading] = useState(false);
  const [bookedHours, setBookedHours] = useState<string[]>([]);
  const [isAllDayBooked, setIsAllDayBooked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const businessStart = settings?.business_hours_start || '09:00:00';
  const businessEnd = settings?.business_hours_end || '18:00:00';
  const slotDuration = settings?.time_slot_duration || 60;

  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlots();
    }
  }, [selectedDate]);

  const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    const [startHour, startMin] = businessStart.split(':').map(Number);
    const [endHour, endMin] = businessEnd.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    for (let minutes = startMinutes; minutes < endMinutes; minutes += slotDuration) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeStr);
    }
    
    return slots;
  };

  const availableTimeSlots = generateTimeSlots();

  const fetchBookedSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      console.log('TimeSlotPicker: Fetching booked slots for', formattedDate);
      
      const data = await fetchBookedTimeSlots(formattedDate);
      console.log('TimeSlotPicker: Received data', data);
      setBookedHours(data.booked_hours || []);
      setIsAllDayBooked(data.is_all_day_booked || false);
    } catch (error) {
      console.error('Error fetching booked slots:', error);
      setError('Failed to load available times. Please try again.');
      setBookedHours([]);
      setIsAllDayBooked(false);
    } finally {
      setLoading(false);
    }
  };

  const isTimeSlotBooked = (time: string) => {
    if (isAllDayBooked) return true;
    const [hour] = time.split(':');
    return bookedHours.includes(hour + ':00');
  };

  const isTimeSlotPassed = (time: string) => {
    const today = startOfToday();
    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
    
    if (!isToday) return false;

    const currentTime = new Date();
    const [hours, minutes] = time.split(':');
    const slotTime = new Date();
    slotTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    return isBefore(slotTime, currentTime);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-48 space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <p className="text-gray-600 dark:text-gray-400">Checking available times...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-error-600 dark:text-error-400">
        {error}
        <button
          onClick={fetchBookedSlots}
          className="block mx-auto mt-4 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isAllDayBooked) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        This day is fully booked. Please select another date.
      </div>
    );
  }

  const availableSlots = availableTimeSlots.filter(time => !isTimeSlotBooked(time) && !isTimeSlotPassed(time));

  if (availableSlots.length === 0) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        No available time slots for this date. Please select another date.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {availableTimeSlots.map((time) => {
        const isBooked = isTimeSlotBooked(time);
        const isPassed = isTimeSlotPassed(time);
        const isDisabled = isBooked || isPassed;
        const formattedTime = format(parse(time, 'HH:mm', new Date()), 'h:mm a');
        const [hours] = time.split(':');
        const session = parseInt(hours) < 12 ? 'Morning' : parseInt(hours) < 17 ? 'Afternoon' : 'Evening';
        
        return (
          <button
            key={time}
            onClick={() => !isDisabled && onTimeSelect(time)}
            disabled={isDisabled}
            className={`
              p-4 rounded-lg text-center transition-colors relative
              ${isDisabled
                ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-50'
                : 'bg-white dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
              }
            `}
          >
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {session}
            </div>
            <div className="text-lg font-medium">
              {formattedTime}
            </div>
            {isDisabled && (
              <div className={`mt-1 text-xs ${
                isPassed ? 'text-gray-500' : 'text-error-500'
              }`}>
                {isPassed ? 'Passed' : 'Booked'}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default TimeSlotPicker;
